import { useEffect, useRef, useState } from "react";
import {
  Camera,
  RefreshCcw,
  X,
  Check,
  ScanFace,
  ShieldAlert,
  ShieldOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import * as blazeface from "@tensorflow-models/blazeface";
import "@tensorflow/tfjs";

/**
 * Selfie capture with AI face verification.
 * - Loads BlazeFace model from LOCAL /models/blazeface/model.json (bundled in
 *   /app/frontend/public/models/blazeface) → no CORS, no TFHub round-trip.
 * - Loop scans <video> stream for human faces and gates the "Ambil Foto" button.
 * - Safety net: if model load exceeds 15s the user can bypass verification.
 */

// Local, self-hosted model — avoids CORS + external network dependency.
const LOCAL_MODEL_URL = `${process.env.PUBLIC_URL || ""}/models/blazeface/model.json`;
const LOAD_TIMEOUT_MS = 15000;

// Module-level singleton so we don't reload the model every time the modal opens.
let _blazefacePromise = null;
function loadModel() {
  if (!_blazefacePromise) {
    _blazefacePromise = blazeface
      .load({ maxFaces: 1, modelUrl: LOCAL_MODEL_URL })
      .catch((err) => {
        // Allow next call to retry if the load fails once.
        _blazefacePromise = null;
        throw err;
      });
  }
  return _blazefacePromise;
}

export function CameraCapture({ open, onClose, onCapture, testIdPrefix = "camera" }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const modelRef = useRef(null);
  const rafRef = useRef(null);
  const detectingRef = useRef(false);
  const [error, setError] = useState("");
  const [captured, setCaptured] = useState(null);
  const [starting, setStarting] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const [modelError, setModelError] = useState("");
  const [faceDetected, setFaceDetected] = useState(false);
  // When user bypasses face verification (after >15s of failed loading), we skip
  // the AI gate entirely and let them capture.
  const [bypassed, setBypassed] = useState(false);
  const [showBypass, setShowBypass] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function start() {
      setStarting(true);
      setError("");
      setModelError("");
      setCaptured(null);
      setFaceDetected(false);
      setModelReady(false);
      setBypassed(false);
      setShowBypass(false);

      // 15s deadline — surface a "Lewati verifikasi wajah" bypass button.
      const bypassTimer = setTimeout(() => {
        if (!cancelled) setShowBypass(true);
      }, LOAD_TIMEOUT_MS);

      // Kick off model load in parallel with camera warm-up.
      loadModel()
        .then((m) => {
          if (cancelled) return;
          modelRef.current = m;
          setModelReady(true);
          clearTimeout(bypassTimer);
          setShowBypass(false);
        })
        .catch(() => {
          if (cancelled) return;
          setModelError(
            "Model deteksi wajah gagal dimuat. Periksa koneksi internet Anda atau gunakan tombol lewati di bawah."
          );
          // Show bypass immediately when load explicitly fails.
          setShowBypass(true);
        });

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "user" },
            width: { ideal: 720 },
            height: { ideal: 720 },
          },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
      } catch (e) {
        setError(
          "Tidak dapat mengakses kamera. Pastikan Anda memberikan izin kamera pada peramban."
        );
      } finally {
        setStarting(false);
      }

      return () => clearTimeout(bypassTimer);
    }

    start();
    return () => {
      cancelled = true;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      detectingRef.current = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };
  }, [open]);

  // Face detection loop — starts once both camera and model are ready.
  useEffect(() => {
    if (!open || captured || !modelReady || starting || bypassed) return;
    let stopped = false;
    detectingRef.current = true;
    let lastRun = 0;
    const INTERVAL_MS = 400; // ~2.5 fps detection is enough for gating a button.

    async function tick(ts) {
      if (stopped) return;
      if (!lastRun || ts - lastRun >= INTERVAL_MS) {
        lastRun = ts;
        const video = videoRef.current;
        const model = modelRef.current;
        if (video && model && video.readyState >= 2 && video.videoWidth > 0) {
          try {
            const predictions = await model.estimateFaces(video, false);
            if (!stopped) {
              const hasFace = Array.isArray(predictions) && predictions.length > 0;
              setFaceDetected(hasFace);
            }
          } catch {
            // Ignore transient inference errors; keep the loop running.
          }
        }
      }
      if (!stopped) rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      stopped = true;
      detectingRef.current = false;
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [open, captured, modelReady, starting, bypassed]);

  if (!open) return null;

  const canShoot = bypassed || (modelReady && faceDetected);

  function shoot() {
    if (!canShoot) return; // Hard guard — button is disabled anyway.
    const video = videoRef.current;
    if (!video) return;
    const w = video.videoWidth || 480;
    const h = video.videoHeight || 480;
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.translate(w, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0, w, h);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.82);
    setCaptured(dataUrl);
  }

  function retry() {
    setCaptured(null);
    setFaceDetected(false);
  }

  function confirm() {
    if (captured) onCapture(captured);
  }

  function activateBypass() {
    setBypassed(true);
    setShowBypass(false);
  }

  const showFaceOverlay = !captured && !error && !starting;
  const shootDisabled = !!error || starting || !canShoot;

  return (
    <div
      className="fixed inset-0 z-[60] bg-slate-950/70 backdrop-blur-sm p-3 sm:p-8 flex items-center justify-center"
      data-testid={`${testIdPrefix}-modal`}
    >
      <div className="w-[calc(100vw-1rem)] max-w-md max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-2xl bg-white p-4 sm:p-6 border border-slate-200 shadow-2xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-teal-700">
              Selfie Absensi
            </div>
            <div className="font-display text-lg font-extrabold text-slate-900 mt-0.5">
              Verifikasi Wajah
            </div>
          </div>
          <button
            className="p-2 rounded-md hover:bg-slate-100 text-slate-500"
            onClick={onClose}
            data-testid={`${testIdPrefix}-close`}
          >
            <X size={18} />
          </button>
        </div>

        <div className="relative aspect-square rounded-xl overflow-hidden bg-slate-900 border border-slate-200">
          {captured ? (
            <img
              src={captured}
              alt="Preview"
              className="w-full h-full object-cover"
              data-testid={`${testIdPrefix}-preview`}
            />
          ) : (
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              style={{ transform: "scaleX(-1)" }}
              playsInline
              muted
              data-testid={`${testIdPrefix}-video`}
            />
          )}

          {/* Face detection overlay ring */}
          {showFaceOverlay && !bypassed ? (
            <div
              className="pointer-events-none absolute inset-0 flex items-center justify-center transition-colors duration-300"
              data-testid={`${testIdPrefix}-face-overlay`}
            >
              <div
                className={`w-2/3 aspect-square rounded-full border-[3px] transition-all duration-300 ${
                  faceDetected
                    ? "border-emerald-400 shadow-[0_0_0_9999px_rgba(0,0,0,0.35)]"
                    : "border-rose-400/80 shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
                }`}
              />
            </div>
          ) : null}

          {/* Status pill (top-left) */}
          {showFaceOverlay ? (
            <div className="absolute top-2 left-2 flex flex-col gap-1.5 max-w-[70%]">
              {bypassed ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-amber-500/95 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 backdrop-blur-sm"
                  data-testid={`${testIdPrefix}-bypassed`}
                >
                  <ShieldOff size={11} /> Verifikasi Dilewati
                </span>
              ) : !modelReady && !modelError ? (
                <span
                  className="inline-flex items-center gap-1.5 rounded-full bg-slate-900/75 text-white text-[10px] font-semibold px-2.5 py-1 backdrop-blur-sm"
                  data-testid={`${testIdPrefix}-model-loading`}
                >
                  <span className="h-2 w-2 rounded-full bg-amber-300 animate-pulse" />
                  Memuat model AI…
                </span>
              ) : null}
              {modelReady && !bypassed ? (
                faceDetected ? (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/90 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 backdrop-blur-sm"
                    data-testid={`${testIdPrefix}-face-ok`}
                  >
                    <ScanFace size={11} /> Wajah Terdeteksi
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-full bg-rose-600/90 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 backdrop-blur-sm"
                    data-testid={`${testIdPrefix}-face-missing`}
                  >
                    <ShieldAlert size={11} /> Wajah tidak terdeteksi
                  </span>
                )
              ) : null}
            </div>
          ) : null}

          {starting && !error && !captured ? (
            <div className="absolute inset-0 flex items-center justify-center text-white text-xs bg-black/40">
              Membuka kamera…
            </div>
          ) : null}
          {error ? (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-white text-sm bg-black/70">
              {error}
            </div>
          ) : null}
        </div>

        {/* Helper text below the preview */}
        {!captured && !error ? (
          bypassed ? (
            <p
              className="mt-3 text-[11px] text-amber-800 text-center font-semibold"
              data-testid={`${testIdPrefix}-bypass-note`}
            >
              Verifikasi wajah dilewati. Silakan ambil foto seperti biasa.
            </p>
          ) : modelError ? (
            <div
              className="mt-3 flex items-start gap-2 text-[11px] rounded-md border border-rose-200 bg-rose-50 text-rose-800 p-2.5"
              data-testid={`${testIdPrefix}-model-error`}
            >
              <ShieldAlert size={13} className="mt-0.5 shrink-0" />
              {modelError}
            </div>
          ) : !modelReady ? (
            <p className="mt-3 text-[11px] text-slate-500 text-center">
              Menyiapkan verifikasi wajah otomatis…
            </p>
          ) : !faceDetected ? (
            <p
              className="mt-3 text-[11px] text-rose-700 text-center font-semibold"
              data-testid={`${testIdPrefix}-face-warning-text`}
            >
              Posisikan wajah Anda tepat di dalam lingkaran & pastikan
              pencahayaan cukup.
            </p>
          ) : (
            <p className="mt-3 text-[11px] text-emerald-700 text-center font-semibold">
              Bagus! Wajah terlihat jelas — silakan ambil foto.
            </p>
          )
        ) : null}

        {/* Bypass button — visible only after 15s of failed loading OR explicit
            model error. Shown before capture and only when not yet bypassed. */}
        {!captured && !error && !bypassed && showBypass ? (
          <button
            onClick={activateBypass}
            className="mt-2 w-full text-xs font-semibold text-amber-800 hover:text-amber-900 underline underline-offset-2"
            data-testid={`${testIdPrefix}-bypass-btn`}
          >
            Model AI lambat memuat — Lewati verifikasi wajah
          </button>
        ) : null}

        <div className="mt-4 flex flex-col sm:flex-row gap-2 sm:gap-3">
          {captured ? (
            <>
              <Button
                variant="outline"
                onClick={retry}
                className="flex-1"
                data-testid={`${testIdPrefix}-retake`}
              >
                <RefreshCcw size={16} className="mr-2" /> Ulangi
              </Button>
              <Button
                onClick={confirm}
                className="flex-1 bg-teal-700 hover:bg-teal-800"
                data-testid={`${testIdPrefix}-confirm`}
              >
                <Check size={16} className="mr-2" /> Gunakan Foto
              </Button>
            </>
          ) : (
            <Button
              onClick={shoot}
              disabled={shootDisabled}
              className="flex-1 bg-teal-700 hover:bg-teal-800 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid={`${testIdPrefix}-shoot`}
            >
              <Camera size={16} className="mr-2" />
              {bypassed
                ? "Ambil Foto"
                : !modelReady && !modelError
                ? "Menyiapkan…"
                : faceDetected
                ? "Ambil Foto"
                : "Wajah tidak terdeteksi"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default CameraCapture;
